import React, { useState, useEffect } from "react";
import { Save } from "lucide-react";
import { Loader } from "../components/PageHead";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";

// Role × module permission matrix editor. Manager is always full-access (locked).
export default function PermissionsMatrix() {
  const { can } = useAuth();
  const canEdit = can("settings", "edit");
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState("");

  useEffect(() => {
    api.get("/permissions")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiErr(e)));
  }, []);

  if (!data) return <Loader />;

  const toggle = (role, module, action) => {
    if (role === "manager") return;
    setData((d) => {
      const m = JSON.parse(JSON.stringify(d.matrix));
      m[role][module][action] = !m[role][module][action];
      if (action === "edit" && m[role][module][action]) m[role][module].view = true;
      if (action === "view" && !m[role][module][action]) m[role][module].edit = false;
      return { ...d, matrix: m };
    });
  };

  const saveRole = async (role) => {
    setSaving(role);
    try { await api.put(`/permissions/${role}`, { modules: data.matrix[role] }); toast.success(`Permissions saved for ${role}`); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(""); }
  };

  return (
    <div className="card pad" style={{ overflowX: "auto" }}>
      <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 14 }}>
        Toggle which modules each role can <b>view</b> and <b>edit</b>. Manager always has full access.
        Changes apply on next sign-in / refresh.
      </p>
      <table className="dt" style={{ fontSize: 12.5 }}>
        <thead>
          <tr>
            <th>Module</th>
            {data.roles.map((r) => <th key={r} style={{ textAlign: "center", textTransform: "capitalize" }}>{r}</th>)}
          </tr>
        </thead>
        <tbody>
          {data.modules.map((mod) => (
            <tr key={mod}>
              <td className="t-strong" style={{ textTransform: "capitalize" }}>{mod.replace(/_/g, " ")}</td>
              {data.roles.map((role) => {
                const cell = data.matrix[role][mod];
                const dis = role === "manager" || !canEdit;
                return (
                  <td key={role} style={{ textAlign: "center" }}>
                    <div style={{ display: "inline-flex", gap: 8 }}>
                      <label title="View" style={{ display: "inline-flex", alignItems: "center", gap: 3, cursor: dis ? "default" : "pointer", opacity: dis ? 0.6 : 1 }}>
                        <input type="checkbox" checked={!!cell.view} disabled={dis} onChange={() => toggle(role, mod, "view")} data-testid={`perm-${role}-${mod}-view`} />
                        <span style={{ fontSize: 10, color: "var(--text-3)" }}>V</span>
                      </label>
                      <label title="Edit" style={{ display: "inline-flex", alignItems: "center", gap: 3, cursor: dis ? "default" : "pointer", opacity: dis ? 0.6 : 1 }}>
                        <input type="checkbox" checked={!!cell.edit} disabled={dis} onChange={() => toggle(role, mod, "edit")} data-testid={`perm-${role}-${mod}-edit`} />
                        <span style={{ fontSize: 10, color: "var(--text-3)" }}>E</span>
                      </label>
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {canEdit && (
        <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
          {data.roles.filter((r) => r !== "manager").map((r) => (
            <button key={r} className="btn ghost sm" onClick={() => saveRole(r)} disabled={saving === r} data-testid={`save-perms-${r}`}>
              <Save size={13} /> Save {r}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
